import jsPDF from 'jspdf';
import { generatePattern } from '@/lib/drafting';

export const ROLL_WIDTH_MM = 594;
const INCH_TO_MM = 25.4;
const POINT_TO_MM = 25.4 / 72;
const MARGIN_MM = 12;
const COMPACT_GAP_MM = 6;
const LABEL_INSET_DEFAULTS_INCH = {
  front: 3,
  back: 3,
  sleeve: 3,
  yoke: 0,
};
export const GRAIN_LABEL_FONT_PT = 42;
export const GUIDE_LABEL_FONT_PT = 24;
export const GRAIN_LABEL_FONT_MM = GRAIN_LABEL_FONT_PT * POINT_TO_MM;
export const GUIDE_LABEL_FONT_MM = GUIDE_LABEL_FONT_PT * POINT_TO_MM;
export const PDF_STROKE_MM = {
  main: 0.55,
  guide: 0.45,
  notch: 0.52,
};
export const PIECE_COLOR_PRESETS = {
  composite_dark_navy: { label: 'Composite Dark Navy', c: 58, m: 36, y: 0, k: 71 },
  composite_deep_teal: { label: 'Composite Deep Teal', c: 59, m: 0, y: 6, k: 71 },
  composite_burgundy: { label: 'Composite Burgundy', c: 0, m: 50, y: 36, k: 71 },
  full_black: { label: 'Full Black', c: 0, m: 0, y: 0, k: 100 },
};
export const DEFAULT_PRINT_PIECE_SETTINGS = {
  visible: true,
  useDefaultLabel: true,
  labelTextOverride: '',
  labelFontPt: 46,
  labelOffsetXmm: 0,
  labelOffsetYmm: 0,
  colorPreset: 'full_black',
  colorCmyk: PIECE_COLOR_PRESETS.full_black,
};

const clampCmykChannel = (value, fallback = 0) => {
  const numeric = Number(value);
  const safe = Number.isFinite(numeric) ? numeric : fallback;
  return Math.max(0, Math.min(100, Math.round(safe)));
};

const normalizeCmyk = (value, fallback = PIECE_COLOR_PRESETS.full_black) => ({
  c: clampCmykChannel(value?.c, fallback.c),
  m: clampCmykChannel(value?.m, fallback.m),
  y: clampCmykChannel(value?.y, fallback.y),
  k: clampCmykChannel(value?.k, fallback.k),
});

const isShirtCollarPrintPiece = (key) => key === 'collar' || key === 'collarLeaf';

export function resolvePiecePrintSettings(piece = {}, defaults = {}) {
  const merged = {
    ...DEFAULT_PRINT_PIECE_SETTINGS,
    ...defaults,
    ...piece,
  };
  const presetKey = merged.colorPreset === 'custom'
    ? 'custom'
    : (Object.prototype.hasOwnProperty.call(PIECE_COLOR_PRESETS, merged.colorPreset)
      ? merged.colorPreset
      : DEFAULT_PRINT_PIECE_SETTINGS.colorPreset);
  const preset = PIECE_COLOR_PRESETS[presetKey];

  const pieceHasVisible = Object.prototype.hasOwnProperty.call(piece, 'visible') && piece.visible !== undefined;
  const defaultsHasVisible = Object.prototype.hasOwnProperty.call(defaults, 'visible') && defaults.visible !== undefined;
  const explicitVisible = pieceHasVisible
    ? piece.visible
    : (defaultsHasVisible ? defaults.visible : undefined);

  return {
    ...merged,
    visible: isShirtCollarPrintPiece(merged.key)
      ? explicitVisible === true
      : explicitVisible !== false,
    useDefaultLabel: merged.useDefaultLabel !== false,
    labelTextOverride: String(merged.labelTextOverride || ''),
    labelFontPt: Math.max(8, Math.min(120, Number(merged.labelFontPt) || DEFAULT_PRINT_PIECE_SETTINGS.labelFontPt)),
    labelOffsetXmm: Math.max(-300, Math.min(300, Number(merged.labelOffsetXmm) || 0)),
    labelOffsetYmm: Math.max(-300, Math.min(300, Number(merged.labelOffsetYmm) || 0)),
    colorPreset: presetKey,
    colorCmyk: normalizeCmyk(merged.colorPreset === 'custom' ? merged.colorCmyk : preset, PIECE_COLOR_PRESETS.full_black),
  };
}

export function isPrintPieceVisible(piece = {}) {
  return isShirtCollarPrintPiece(piece?.key)
    ? piece?.visible === true
    : piece?.visible !== false;
}

export function resolvePieceLabelText(currentPattern, piece = {}) {
  const defaultText = formatPatternLabel(currentPattern);
  if (piece?.useDefaultLabel === false) {
    const overrideText = String(piece?.labelTextOverride || '').trim();
    return overrideText || defaultText;
  }
  return defaultText;
}

export function cmykToRgbHex(cmyk) {
  const safe = normalizeCmyk(cmyk);
  const c = safe.c / 100;
  const m = safe.m / 100;
  const y = safe.y / 100;
  const k = safe.k / 100;
  const r = Math.round(255 * (1 - c) * (1 - k));
  const g = Math.round(255 * (1 - m) * (1 - k));
  const b = Math.round(255 * (1 - y) * (1 - k));
  return `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

export function resolvePieceStrokeColor(piece = {}, fallbackHex = '#1f2937') {
  const settings = resolvePiecePrintSettings(piece);
  if (settings.colorPreset === 'custom') {
    return {
      hex: cmykToRgbHex(settings.colorCmyk),
      cmyk: settings.colorCmyk,
    };
  }

  const preset = PIECE_COLOR_PRESETS[settings.colorPreset];
  if (!preset) return { hex: fallbackHex, cmyk: null };
  return {
    hex: cmykToRgbHex(preset),
    cmyk: preset,
  };
}

function setDocCmykColor(doc, cmyk) {
  const safe = normalizeCmyk(cmyk);
  const values = [safe.c, safe.m, safe.y, safe.k].map((value) => (value / 100).toFixed(4));
  doc.setDrawColor(values[0], values[1], values[2], values[3]);
}

function setDocCmykFillColor(doc, cmyk) {
  const safe = normalizeCmyk(cmyk);
  const values = [safe.c, safe.m, safe.y, safe.k].map((value) => (value / 100).toFixed(4));
  doc.setFillColor(values[0], values[1], values[2], values[3]);
}

function setDocCmykTextColor(doc, cmyk) {
  const safe = normalizeCmyk(cmyk);
  const values = [safe.c, safe.m, safe.y, safe.k].map((value) => (value / 100).toFixed(4));
  doc.setTextColor(values[0], values[1], values[2], values[3]);
}

function resolveGlobalExportStroke(exportOptions = {}) {
  const presetKey = exportOptions?.colorPreset;
  if (presetKey && PIECE_COLOR_PRESETS[presetKey]) {
    const preset = PIECE_COLOR_PRESETS[presetKey];
    return {
      presetKey,
      cmyk: normalizeCmyk(preset, PIECE_COLOR_PRESETS.full_black),
      hex: cmykToRgbHex(preset),
    };
  }

  if (exportOptions?.colorCmyk) {
    const custom = normalizeCmyk(exportOptions.colorCmyk, PIECE_COLOR_PRESETS.full_black);
    return {
      presetKey: 'custom',
      cmyk: custom,
      hex: cmykToRgbHex(custom),
    };
  }

  return {
    presetKey: 'full_black',
    cmyk: normalizeCmyk(PIECE_COLOR_PRESETS.full_black, PIECE_COLOR_PRESETS.full_black),
    hex: cmykToRgbHex(PIECE_COLOR_PRESETS.full_black),
  };
}

export function resolvePieceLabelInsetInches(pieceKey, value) {
  const fallback = LABEL_INSET_DEFAULTS_INCH[pieceKey] ?? 3;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.min(12, Math.round(numeric * 2) / 2));
}

function withResolvedLabelInset(layoutPieces = []) {
  return layoutPieces.map((piece) => ({
    ...piece,
    labelInsetInches: resolvePieceLabelInsetInches(piece.key, piece.labelInsetInches),
  }));
}

function getShortClientName(clientName = '') {
  const parts = String(clientName || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[1][0].toUpperCase()}.`;
}

export function formatPatternLabel(currentPattern) {
  const patternNumber = String(currentPattern?.pattern_number || '').trim();
  const shortName = getShortClientName(currentPattern?.client_name || '');
  return [patternNumber, shortName].filter(Boolean).join(' ').trim();
}

function parsePathCommands(pathData = '') {
  return pathData.match(/[a-zA-Z][^a-zA-Z]*/g) || [];
}

function extractPathPoints(pathData = '', curveSteps = 12) {
  const commands = parsePathCommands(pathData);
  if (commands.length === 0) return [];

  let current = { x: 0, y: 0 };
  let start = null;
  const points = [];

  commands.forEach((command) => {
    const type = command[0];
    const args = command
      .slice(1)
      .trim()
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number);

    if (type === 'M') {
      current = { x: args[0], y: args[1] };
      start = { ...current };
      points.push({ ...current });
    } else if (type === 'L') {
      current = { x: args[0], y: args[1] };
      points.push({ ...current });
    } else if (type === 'Q') {
      const p0 = { ...current };
      const p1 = { x: args[0], y: args[1] };
      const p2 = { x: args[2], y: args[3] };

      for (let i = 1; i <= curveSteps; i += 1) {
        const t = i / curveSteps;
        const inv = 1 - t;
        points.push({
          x: inv * inv * p0.x + 2 * inv * t * p1.x + t * t * p2.x,
          y: inv * inv * p0.y + 2 * inv * t * p1.y + t * t * p2.y,
        });
      }

      current = p2;
    } else if (type === 'C') {
      const p0 = { ...current };
      const p1 = { x: args[0], y: args[1] };
      const p2 = { x: args[2], y: args[3] };
      const p3 = { x: args[4], y: args[5] };

      for (let i = 1; i <= curveSteps; i += 1) {
        const t = i / curveSteps;
        const inv = 1 - t;
        points.push({
          x: inv ** 3 * p0.x
            + 3 * inv ** 2 * t * p1.x
            + 3 * inv * t ** 2 * p2.x
            + t ** 3 * p3.x,
          y: inv ** 3 * p0.y
            + 3 * inv ** 2 * t * p1.y
            + 3 * inv * t ** 2 * p2.y
            + t ** 3 * p3.y,
        });
      }

      current = p3;
    } else if (type === 'Z' && start) {
      points.push({ ...start });
      current = { ...start };
    }
  });

  return points;
}

function getBounds(points = []) {
  if (points.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  }

  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

function getPieceBoundsPaths(piece = {}) {
  if (Array.isArray(piece?.boundsPaths) && piece.boundsPaths.length) {
    return piece.boundsPaths.filter(Boolean);
  }
  return [piece?.path].filter(Boolean);
}

function extractPieceBoundsPoints(piece = {}) {
  return getPieceBoundsPaths(piece).flatMap((path) => extractPathPoints(path));
}

function normalizeMmPoints(piece) {
  const rawPoints = extractPieceBoundsPoints(piece);
  return rawPoints.map((pt) => ({
    x: (pt.x - piece.bounds.minX) * INCH_TO_MM,
    y: (pt.y - piece.bounds.minY) * INCH_TO_MM,
  }));
}

function rotatePoints(points, angle) {
  return points.map((pt) => rotatePoint(pt.x, pt.y, angle));
}

function translatePoints(points, tx, ty) {
  return points.map((pt) => ({ x: pt.x + tx, y: pt.y + ty }));
}

function isPointInPolygon(point, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const intersect = ((yi > point.y) !== (yj > point.y))
      && (point.x < ((xj - xi) * (point.y - yi)) / ((yj - yi) || 1e-9) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

function segmentsIntersect(p1, p2, p3, p4) {
  const det = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  const d1 = det(p1, p2, p3);
  const d2 = det(p1, p2, p4);
  const d3 = det(p3, p4, p1);
  const d4 = det(p3, p4, p2);
  return d1 * d2 < 0 && d3 * d4 < 0;
}

function polygonsIntersect(polyA, polyB) {
  if (polyA.length < 3 || polyB.length < 3) return false;

  for (let i = 0; i < polyA.length; i += 1) {
    const a1 = polyA[i];
    const a2 = polyA[(i + 1) % polyA.length];
    for (let j = 0; j < polyB.length; j += 1) {
      const b1 = polyB[j];
      const b2 = polyB[(j + 1) % polyB.length];
      if (segmentsIntersect(a1, a2, b1, b2)) return true;
    }
  }

  if (isPointInPolygon(polyA[0], polyB)) return true;
  if (isPointInPolygon(polyB[0], polyA)) return true;
  return false;
}

export function getExportPieces(patternData) {
  const hasTwoPieceSleeve = Boolean(patternData?.topSleevePath || patternData?.underSleevePath);

  return [
    { key: 'front', label: 'Front', path: patternData?.frontPath || '' },
    { key: 'side', label: 'Side', path: patternData?.sidePath || '' },
    { key: 'back', label: 'Back', path: patternData?.backPath || '' },
    { key: 'yoke', label: 'Yoke', path: patternData?.yokePath || '' },
    { key: 'topSleeve', label: 'Top Sleeve', path: patternData?.topSleevePath || '' },
    { key: 'underSleeve', label: 'Under Sleeve', path: patternData?.underSleevePath || '' },
    { key: 'sleeve', label: 'Sleeve', path: hasTwoPieceSleeve ? '' : (patternData?.sleevePath || '') },
    { key: 'collar', label: 'Collar', path: patternData?.collarBandLayoutPath || patternData?.collarBandPath || '', visible: false },
    { key: 'collarLeaf', label: 'Collar Leaf', path: patternData?.collarLeafLayoutPath || '', visible: false },
  ]
    .filter((piece) => piece.path)
    .map((piece) => {
      const pathOverlays = getPiecePathOverlays(patternData, piece.key);
      const boundsPaths = [piece.path, ...pathOverlays.map((overlay) => overlay.d)].filter(Boolean);
      const points = boundsPaths.flatMap((path) => extractPathPoints(path));
      return {
        ...piece,
        boundsPaths,
        bounds: getBounds(points),
      };
    });
}

function rotatePoint(x, y, angleDeg) {
  const angle = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    x: x * cos - y * sin,
    y: x * sin + y * cos,
  };
}

export function transformPointToLayout(point, pieceLayout, pieceBounds) {
  let normalizedX = (point.x - pieceBounds.minX) * INCH_TO_MM;
  let normalizedY = (point.y - pieceBounds.minY) * INCH_TO_MM;

  if (pieceLayout.flipHorizontal) {
    normalizedX = -normalizedX;
  }
  if (pieceLayout.flipVertical) {
    normalizedY = -normalizedY;
  }

  const rotated = rotatePoint(normalizedX, normalizedY, Number(pieceLayout.rotation) || 0);

  return {
    x: (Number(pieceLayout.x) || 0) + rotated.x,
    y: (Number(pieceLayout.y) || 0) + rotated.y,
  };
}

function transformPoint(point, pieceLayout, pieceBounds) {
  return transformPointToLayout(point, pieceLayout, pieceBounds);
}

function parseLineEndpoints(pathData = '') {
  const commands = parsePathCommands(pathData);
  let start = null;
  let end = null;

  commands.forEach((command) => {
    const type = command[0];
    const args = command
      .slice(1)
      .trim()
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number);

    if (type === 'M' && args.length >= 2) {
      start = { x: args[0], y: args[1] };
    }
    if (type === 'L' && args.length >= 2) {
      end = { x: args[0], y: args[1] };
    }
  });

  return start && end ? { start, end } : null;
}

export function getPieceGrainLine(patternData, pieceKey) {
  if (pieceKey === 'front' && patternData?.fCreasePath) {
    return parseLineEndpoints(patternData.fCreasePath);
  }
  if (pieceKey === 'back' && patternData?.bCreasePath) {
    return parseLineEndpoints(patternData.bCreasePath);
  }
  return null;
}

function getNamedPointInPathCoords(patternData, name) {
  const named = Array.isArray(patternData?.namedPoints)
    ? patternData.namedPoints.find((point) => point.name === name)
    : null;

  if (!named) return null;
  return { x: named.x, y: -named.y };
}

function formatPathNumber(value) {
  return Number(value).toFixed(4).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
}

function toPathPoint(point) {
  return `${formatPathNumber(point.x)} ${formatPathNumber(point.y)}`;
}

function buildPolylinePathData(points = []) {
  const validPoints = points.filter(Boolean);
  if (validPoints.length < 2) return '';
  return validPoints
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${toPathPoint(point)}`)
    .join(' ');
}

function stripInitialMoveCommand(pathData = '') {
  const commands = parsePathCommands(pathData);
  if (commands.length <= 1) return '';
  return commands.slice(1).join(' ').trim();
}

function getJacketFrontMeasurementGuides(patternData) {
  const guides = Array.isArray(patternData?.frontMeasurements)
    ? patternData.frontMeasurements
    : [];
  const guideIdsByLabel = {
    'Front Chest': 'jacket-front-chest-measurement',
    'Across Front': 'jacket-front-x-front-measurement',
    'Front Waist': 'jacket-front-waist-measurement',
    'Front Belly': 'jacket-front-belly-measurement',
    'Front Seat': 'jacket-front-hem-measurement',
  };

  return guides
    .filter((guide) => Object.prototype.hasOwnProperty.call(guideIdsByLabel, guide.label))
    .map((guide) => ({
      ...guide,
      id: guide.id || guideIdsByLabel[guide.label],
      type: 'measurement',
    }));
}

function getJacketBackMeasurementGuides(patternData) {
  const guides = Array.isArray(patternData?.backMeasurements)
    ? patternData.backMeasurements
    : [];
  return guides
    .filter((guide) => guide.label !== 'Back Scye Vertical')
    .map((guide, index) => ({
      ...guide,
      id: guide.id || `jacket-back-measurement-${index}`,
      type: 'measurement',
    }));
}

function getJacketSideMeasurementGuides(patternData) {
  const guides = Array.isArray(patternData?.sideMeasurements)
    ? patternData.sideMeasurements
    : [];
  return guides.map((guide, index) => ({
    ...guide,
    id: guide.id || `jacket-side-measurement-${index}`,
    type: 'measurement',
  }));
}

function getTrouserMeasurementGuides(patternData, pieceKey) {
  const frontGuides = Array.isArray(patternData?.frontMeasurements)
    ? patternData.frontMeasurements
    : [];
  const backGuides = Array.isArray(patternData?.backMeasurements)
    ? patternData.backMeasurements
    : [];
  const hemShoeShapeEnabled = patternData?.hemShoeShapeEnabled !== undefined
    ? Boolean(patternData.hemShoeShapeEnabled)
    : Boolean(patternData?.measurements?.hemShoeShapeEnabled);

  const guideConfig = pieceKey === 'front'
    ? {
        source: frontGuides,
        labels: {
          FH: { id: 'trouser-front-seat-measurement', label: 'Seat' },
          FT: { id: 'trouser-front-thigh-measurement', label: 'Thigh' },
          FK: { id: 'trouser-front-knee-measurement', label: 'Knee' },
          FHem: { id: 'trouser-front-hem-measurement', label: 'Hem' },
        },
      }
    : {
        source: backGuides,
        labels: {
          BH: { id: 'trouser-back-seat-measurement', label: 'Seat' },
          BT: { id: 'trouser-back-thigh-measurement', label: 'Thigh' },
          BK: { id: 'trouser-back-knee-measurement', label: 'Knee' },
          BHem: { id: 'trouser-back-hem-measurement', label: 'Hem' },
        },
      };

  const filtered = guideConfig.source
    .filter((guide) => !(hemShoeShapeEnabled && guide.label === (pieceKey === 'front' ? 'FHem' : 'BHem')))
    .filter((guide) => Object.prototype.hasOwnProperty.call(guideConfig.labels, guide.label))
    .map((guide) => ({
      ...guide,
      id: guide.id || guideConfig.labels[guide.label].id,
      label: guideConfig.labels[guide.label].label,
      type: 'measurement',
    }));

  if (filtered.length) {
    return filtered;
  }

  const source = Array.isArray(patternData?.constructionLines)
    ? patternData.constructionLines
    : [];
  const fallbackIndexes = pieceKey === 'front' ? [0, 1, 4] : [2, 3, 5];
  return fallbackIndexes
    .map((index) => source[index])
    .filter(Boolean)
    .map((line, index) => {
      const baseLabel = (line.label || '').replace(' Level', '');
      const label = baseLabel.toLowerCase().includes('crotch') ? 'Thigh' : baseLabel;
      return {
        ...line,
        id: `trouser-${pieceKey}-fallback-guide-${index}`,
        label,
        type: 'measurement',
      };
    });
}

function buildJacketFrontInnerLapelPath(patternData) {
  const fnd = getNamedPointInPathCoords(patternData, 'FND');
  const gp = getNamedPointInPathCoords(patternData, 'GP');
  const cp0 = getNamedPointInPathCoords(patternData, 'CP0');
  return buildPolylinePathData([fnd, gp, cp0]);
}

function reflectPointAcrossLine(point, lineStart, lineEnd) {
  const vx = lineEnd.x - lineStart.x;
  const vy = lineEnd.y - lineStart.y;
  const denom = (vx * vx) + (vy * vy);
  if (denom < 1e-8) return { ...point };

  const t = (((point.x - lineStart.x) * vx) + ((point.y - lineStart.y) * vy)) / denom;
  const projection = {
    x: lineStart.x + (vx * t),
    y: lineStart.y + (vy * t),
  };

  return {
    x: (2 * projection.x) - point.x,
    y: (2 * projection.y) - point.y,
  };
}

function toCanvasGuidePoint(point) {
  return {
    x: point.x,
    y: -point.y,
  };
}

function getSleeveSplitGuide(patternData) {
  const guides = Array.isArray(patternData?.sleeveConstructionLines)
    ? patternData.sleeveConstructionLines
    : [];
  return guides.find((guide) => guide.label === 'US Split Line') || null;
}

function getJacketSleeveMeasurementGuides(patternData, pieceKey) {
  const biB = getNamedPointInPathCoords(patternData, 'BiB');
  const elB = getNamedPointInPathCoords(patternData, 'ElB');
  const biF = getNamedPointInPathCoords(patternData, 'BiF');
  const fp = getNamedPointInPathCoords(patternData, 'FP');
  const tsElF = getNamedPointInPathCoords(patternData, 'TSElF');
  const usElF = getNamedPointInPathCoords(patternData, 'USElF');
  const splitGuide = getSleeveSplitGuide(patternData);
  const usBiF = splitGuide?.p2
    ? { x: splitGuide.p2.x, y: -splitGuide.p2.y }
    : null;
  const tsBiF = usBiF && fp && biF
    ? reflectPointAcrossLine(usBiF, fp, biF)
    : null;

  if (pieceKey === 'topSleeve') {
    const sleeveLengthGuide = patternData?.sleeveLengthGuide?.p1 && patternData?.sleeveLengthGuide?.p2
      ? {
          ...patternData.sleeveLengthGuide,
          id: patternData.sleeveLengthGuide.id || 'jacket-top-sleeve-length-measurement',
          label: patternData.sleeveLengthGuide.label || 'Sleeve Length',
          type: 'measurement',
        }
      : (patternData?.matchingSummary?.jacketSleeveLengthMidpointDrawn && getNamedPointInPathCoords(patternData, 'SLT') && getNamedPointInPathCoords(patternData, 'HB') && getNamedPointInPathCoords(patternData, 'TSHF')
        ? (() => {
            const sltPoint = getNamedPointInPathCoords(patternData, 'SLT');
            const hbPoint = getNamedPointInPathCoords(patternData, 'HB');
            const tshfPoint = getNamedPointInPathCoords(patternData, 'TSHF');
            const midpoint = {
              x: (hbPoint.x + tshfPoint.x) / 2,
              y: (hbPoint.y + tshfPoint.y) / 2,
            };
            return {
              id: 'jacket-top-sleeve-length-measurement',
              label: 'Sleeve Length',
              type: 'measurement',
              p1: toCanvasGuidePoint(sltPoint),
              p2: toCanvasGuidePoint(midpoint),
              showLabel: true,
              value: Number(patternData?.matchingSummary?.jacketSleeveLengthMidpointDrawn || 0),
              labelText: `${Number(patternData?.matchingSummary?.jacketSleeveLengthMidpointDrawn || 0).toFixed(2)}"`,
              labelOffsetMm: 3.4,
            };
          })()
        : null);

    return [
      sleeveLengthGuide,
      biB && tsBiF ? {
        id: 'jacket-top-sleeve-bicep-measurement',
        label: 'Top Sleeve Bicep Line',
        type: 'measurement',
        p1: toCanvasGuidePoint(biB),
        p2: toCanvasGuidePoint(tsBiF),
      } : null,
      elB && tsElF ? {
        id: 'jacket-top-sleeve-elbow-measurement',
        label: 'Top Sleeve Elbow Line',
        type: 'measurement',
        p1: toCanvasGuidePoint(elB),
        p2: toCanvasGuidePoint(tsElF),
      } : null,
    ].filter(Boolean);
  }

  if (pieceKey === 'underSleeve') {
    return [
      biB && usBiF ? {
        id: 'jacket-under-sleeve-bicep-measurement',
        label: 'Under Sleeve Bicep Line',
        type: 'measurement',
        p1: toCanvasGuidePoint(biB),
        p2: toCanvasGuidePoint(usBiF),
      } : null,
      elB && usElF ? {
        id: 'jacket-under-sleeve-elbow-measurement',
        label: 'Under Sleeve Elbow Line',
        type: 'measurement',
        p1: toCanvasGuidePoint(elB),
        p2: toCanvasGuidePoint(usElF),
      } : null,
    ].filter(Boolean);
  }

  return [];
}

function getGuideLabelPlacement(guide, pieceLayout, pieceBounds) {
  if (!guide?.showLabel || !guide?.p1 || !guide?.p2) return null;

  const p1 = transformPoint({ x: guide.p1.x, y: -guide.p1.y }, pieceLayout, pieceBounds);
  const p2 = transformPoint({ x: guide.p2.x, y: -guide.p2.y }, pieceLayout, pieceBounds);
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const length = Math.hypot(dx, dy) || 1;
  const nx = -dy / length;
  const ny = dx / length;
  const offsetMm = Number(guide.labelOffsetMm ?? 3);
  let angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  if (angle > 90 || angle < -90) angle += 180;

  return {
    text: guide.labelText || `${Number(guide.value || 0).toFixed(2)}"`,
    x: ((p1.x + p2.x) / 2) + (nx * offsetMm),
    y: ((p1.y + p2.y) / 2) + (ny * offsetMm),
    angle,
  };
}

function pointToSegmentDistance(point, a, b) {
  const abX = b.x - a.x;
  const abY = b.y - a.y;
  const abLenSq = abX * abX + abY * abY;

  if (abLenSq <= 1e-9) {
    return Math.hypot(point.x - a.x, point.y - a.y);
  }

  const t = Math.max(0, Math.min(1, ((point.x - a.x) * abX + (point.y - a.y) * abY) / abLenSq));
  const projX = a.x + abX * t;
  const projY = a.y + abY * t;
  return Math.hypot(point.x - projX, point.y - projY);
}

function minDistanceToPoints(point, points = []) {
  if (!points.length) return Infinity;
  return Math.min(...points.map((candidate) => Math.hypot(candidate.x - point.x, candidate.y - point.y)));
}

function getPieceLabelLine(patternData, pieceKey, pieceBounds) {
  if (pieceKey === 'yoke') return null;

  const grainLine = getPieceGrainLine(patternData, pieceKey);
  if (grainLine?.start && grainLine?.end) {
    return grainLine;
  }

  if (patternData?.garmentType === 'jacket' && (pieceKey === 'front' || pieceKey === 'side') && pieceBounds) {
    const centerX = (pieceBounds.minX + pieceBounds.maxX) / 2;
    return {
      start: { x: centerX, y: pieceBounds.minY },
      end: { x: centerX, y: pieceBounds.maxY },
    };
  }

  if (pieceKey === 'front') {
    const cf1 = getNamedPointInPathCoords(patternData, 'CF1');
    const cf7 = getNamedPointInPathCoords(patternData, 'CF7');
    if (cf1 && cf7) {
      return { start: cf1, end: cf7 };
    }
  }

  if (pieceKey === 'back') {
    const cb1 = getNamedPointInPathCoords(patternData, 'CB1');
    const cb7 = getNamedPointInPathCoords(patternData, 'CB7');
    if (cb1 && cb7) {
      return { start: cb1, end: cb7 };
    }
  }

  if (pieceKey === 'sleeve') {
    if (patternData?.sleeveLengthPath) {
      const sleeveLengthLine = parseLineEndpoints(patternData.sleeveLengthPath);
      if (sleeveLengthLine?.start && sleeveLengthLine?.end) {
        return sleeveLengthLine;
      }
    }

    if (pieceBounds) {
      const centerX = (pieceBounds.minX + pieceBounds.maxX) / 2;
      return {
        start: { x: centerX, y: pieceBounds.minY },
        end: { x: centerX, y: pieceBounds.maxY },
      };
    }
  }

  if (pieceKey === 'topSleeve' || pieceKey === 'underSleeve') {
    if (pieceBounds) {
      const centerX = (pieceBounds.minX + pieceBounds.maxX) / 2;
      return {
        start: { x: centerX, y: pieceBounds.minY },
        end: { x: centerX, y: pieceBounds.maxY },
      };
    }
  }

  return null;
}

export function getPieceLabelPlacement(patternData, pieceKey, pieceLayout, pieceBounds, piecePath = '') {
  const basePath = piecePath || patternData?.[`${pieceKey}Path`] || '';
  const basePathPoints = extractPathPoints(basePath, 16);
  const basePathBounds = basePathPoints.length ? getBounds(basePathPoints) : pieceBounds;
  const activeLabelBounds = patternData?.garmentType === 'jacket' && (pieceKey === 'front' || pieceKey === 'side')
    ? basePathBounds
    : pieceBounds;
  const centerPoint = {
    x: (activeLabelBounds.minX + activeLabelBounds.maxX) / 2,
    y: (activeLabelBounds.minY + activeLabelBounds.maxY) / 2,
  };

  const insetMm = resolvePieceLabelInsetInches(pieceKey, pieceLayout?.labelInsetInches) * INCH_TO_MM;

  const buildPolygon = () => {
    const polygonRaw = extractPathPoints(basePath, 16);
    if (polygonRaw.length < 3) return null;
    return polygonRaw.map((point) => transformPoint(point, pieceLayout, pieceBounds));
  };

  if (pieceKey === 'yoke') {
    const yokeAngle = Number(pieceLayout?.rotation || 0);
    const center = transformPoint(centerPoint, pieceLayout, pieceBounds);
    if (insetMm <= 0) {
      return {
        x: center.x + (Number(pieceLayout?.labelOffsetXmm) || 0),
        y: center.y + (Number(pieceLayout?.labelOffsetYmm) || 0),
        angle: yokeAngle,
      };
    }

    const polygon = buildPolygon();
    if (!polygon) {
      return {
        x: center.x + (Number(pieceLayout?.labelOffsetXmm) || 0),
        y: center.y + (Number(pieceLayout?.labelOffsetYmm) || 0),
        angle: yokeAngle,
      };
    }

    const up = { x: center.x, y: center.y - insetMm };
    const down = { x: center.x, y: center.y + insetMm };
    const upInside = isPointInPolygon(up, polygon);
    const downInside = isPointInPolygon(down, polygon);

    if (upInside && !downInside) {
      return {
        x: up.x + (Number(pieceLayout?.labelOffsetXmm) || 0),
        y: up.y + (Number(pieceLayout?.labelOffsetYmm) || 0),
        angle: yokeAngle,
      };
    }
    if (downInside && !upInside) {
      return {
        x: down.x + (Number(pieceLayout?.labelOffsetXmm) || 0),
        y: down.y + (Number(pieceLayout?.labelOffsetYmm) || 0),
        angle: yokeAngle,
      };
    }
    if (upInside) {
      return {
        x: up.x + (Number(pieceLayout?.labelOffsetXmm) || 0),
        y: up.y + (Number(pieceLayout?.labelOffsetYmm) || 0),
        angle: yokeAngle,
      };
    }
    return {
      x: center.x + (Number(pieceLayout?.labelOffsetXmm) || 0),
      y: center.y + (Number(pieceLayout?.labelOffsetYmm) || 0),
      angle: yokeAngle,
    };
  }

  const labelLine = getPieceLabelLine(patternData, pieceKey, activeLabelBounds);
  if (!labelLine?.start || !labelLine?.end) {
    const center = transformPoint(centerPoint, pieceLayout, pieceBounds);
    return {
      x: center.x + (Number(pieceLayout?.labelOffsetXmm) || 0),
      y: center.y + (Number(pieceLayout?.labelOffsetYmm) || 0),
      angle: 0,
    };
  }

  const start = transformPoint(labelLine.start, pieceLayout, pieceBounds);
  const end = transformPoint(labelLine.end, pieceLayout, pieceBounds);
  const center = {
    x: (start.x + end.x) / 2,
    y: (start.y + end.y) / 2,
  };

  let resolvedCenter = center;

  if (insetMm > 0) {
    const polygon = buildPolygon();
    const lineDx = end.x - start.x;
    const lineDy = end.y - start.y;
    const lineLen = Math.hypot(lineDx, lineDy);

    if (polygon && lineLen > 1e-6) {
      const nx = -lineDy / lineLen;
      const ny = lineDx / lineLen;

      const candidateA = {
        x: center.x + nx * insetMm,
        y: center.y + ny * insetMm,
      };
      const candidateB = {
        x: center.x - nx * insetMm,
        y: center.y - ny * insetMm,
      };

      const options = [candidateA, candidateB].filter((candidate) => isPointInPolygon(candidate, polygon));

      if (options.length === 1) {
        resolvedCenter = options[0];
      } else if (options.length === 2) {
        const avoidPointsRaw = pieceKey === 'front' && Array.isArray(patternData?.buttons)
          ? patternData.buttons.map((button) => ({ x: button.x, y: button.y }))
          : [];
        const avoidPoints = avoidPointsRaw.map((point) => transformPoint(point, pieceLayout, pieceBounds));

        const scoreOption = (candidate) => {
          const lineClearance = pointToSegmentDistance(candidate, start, end);
          const buttonClearance = minDistanceToPoints(candidate, avoidPoints);
          return lineClearance * 0.8 + buttonClearance * 1.2;
        };

        const best = options
          .map((candidate) => ({ candidate, score: scoreOption(candidate) }))
          .sort((a, b) => b.score - a.score)[0];

        resolvedCenter = best?.candidate || options[0];
      }
    }
  }

  return {
    x: resolvedCenter.x + (Number(pieceLayout?.labelOffsetXmm) || 0),
    y: resolvedCenter.y + (Number(pieceLayout?.labelOffsetYmm) || 0),
    angle: (Math.atan2(end.y - start.y, end.x - start.x) * 180) / Math.PI,
  };
}

function resolvePdfTextAngle(svgAngleDeg) {
  const raw = Number(svgAngleDeg) || 0;
  let signed = ((raw % 360) + 360) % 360;
  if (signed > 180) signed -= 360;
  // SVG preview and jsPDF text rotation directions differ; invert for PDF parity.
  return Number((-signed).toFixed(2));
}

function drawPatternPieceLabel(doc, text, placement, pieceSettings = {}) {
  const fontSizePt = Math.max(8, Math.min(120, Number(pieceSettings?.labelFontPt) || DEFAULT_PRINT_PIECE_SETTINGS.labelFontPt));
  const anglePdf = resolvePdfTextAngle(placement?.angle);

  doc.setFont('courier', 'bold');
  doc.setFontSize(fontSizePt);

  // jsPDF has known bugs combining `align: 'center'`, `baseline: 'middle'`, and `angle`.
  // It causes text to drift around its left edge or shift vertically along the screen Y axis.
  // We completely bypass this by manually calculating the rotated bounding box offsets.
  const textWidthMm = doc.getTextWidth(text);
  const textHeightMm = fontSizePt * (25.4 / 72);

  // The local anchor point offset to center the text from its bottom-left baseline.
  // Left edge offset to reach the horizontal center: -textWidthMm / 2
  // Alphabetic baseline offset to reach the vertical middle: roughly +textHeightMm * 0.32 (since Y goes down)
  const lx = -textWidthMm / 2;
  const ly = textHeightMm * 0.32; 

  const angleDeg = Number(placement?.angle) || 0;
  const rad = (angleDeg * Math.PI) / 180;

  // Rotate local offsets by the screen angle (where positive is clockwise)
  const dx = lx * Math.cos(rad) - ly * Math.sin(rad);
  const dy = lx * Math.sin(rad) + ly * Math.cos(rad);

  doc.text(text, placement.x + dx, placement.y + dy, {
    angle: anglePdf,
  });
}

export function getPieceConstructionGuides(patternData, pieceKey) {
  if (patternData?.garmentType === 'trouser' && (pieceKey === 'front' || pieceKey === 'back')) {
    const trouserGuides = getTrouserMeasurementGuides(patternData, pieceKey);
    const grainLine = getPieceGrainLine(patternData, pieceKey);
    if (grainLine?.start && grainLine?.end) {
      trouserGuides.push({
        p1: {
          x: grainLine.start.x,
          y: -grainLine.start.y,
        },
        p2: {
          x: grainLine.end.x,
          y: -grainLine.end.y,
        },
        label: 'Crease',
        id: `trouser-${pieceKey}-crease-guide`,
        type: 'crease',
      });
    }
    return trouserGuides;
  }

  if (patternData?.garmentType === 'jacket') {
    if (pieceKey === 'topSleeve') {
      const derivedGuides = getJacketSleeveMeasurementGuides(patternData, pieceKey);
      if (derivedGuides.length) return derivedGuides;
      return Array.isArray(patternData?.topSleeveConstructionLines)
        ? patternData.topSleeveConstructionLines
        : [];
    }

    if (pieceKey === 'underSleeve') {
      const derivedGuides = getJacketSleeveMeasurementGuides(patternData, pieceKey);
      if (derivedGuides.length) return derivedGuides;
      return Array.isArray(patternData?.underSleeveConstructionLines)
        ? patternData.underSleeveConstructionLines
        : [];
    }

    if (pieceKey === 'side') {
      return getJacketSideMeasurementGuides(patternData);
    }

    if (pieceKey === 'front') {
      return getJacketFrontMeasurementGuides(patternData);
    }

    if (pieceKey === 'back') {
      return getJacketBackMeasurementGuides(patternData);
    }
  }

  const source = patternData?.constructionLines || [];
  const grainLine = getPieceGrainLine(patternData, pieceKey);
  if (pieceKey === 'sleeve') {
    return Array.isArray(patternData?.sleeveConstructionLines)
      ? patternData.sleeveConstructionLines
      : [];
  }

  if (pieceKey === 'topSleeve') {
    return Array.isArray(patternData?.topSleeveConstructionLines)
      ? patternData.topSleeveConstructionLines
      : [];
  }

  if (pieceKey === 'underSleeve') {
    return Array.isArray(patternData?.underSleeveConstructionLines)
      ? patternData.underSleeveConstructionLines
      : [];
  }

  if (pieceKey === 'side') {
    const sideSource = Array.isArray(patternData?.sideConstructionLines)
      ? patternData.sideConstructionLines
      : [];
    return sideSource;
  }

  if (pieceKey !== 'front' && pieceKey !== 'back') return [];
  const pieceLines = pieceKey === 'front' ? source.slice(0, 3) : source.slice(3, 6);

  const normalizedPieceLines = pieceLines.map((line) => {
    const baseLabel = (line.label || '').replace(' Level', '');
    const label = baseLabel.toLowerCase().includes('crotch') ? 'Thigh' : baseLabel;
    return {
      ...line,
      label,
    };
  });

  if (grainLine?.start && grainLine?.end) {
    normalizedPieceLines.push({
      p1: {
        x: grainLine.start.x,
        y: -grainLine.start.y,
      },
      p2: {
        x: grainLine.end.x,
        y: -grainLine.end.y,
      },
      label: 'Crease',
      type: 'crease',
    });
  }

  return normalizedPieceLines;
}

export function getPiecePathOverlays(patternData, pieceKey) {
  if (patternData?.garmentType !== 'jacket' || pieceKey !== 'front') return [];

  const overlays = [];

  if (patternData?.rollLineGuide?.p1 && patternData?.rollLineGuide?.p2) {
    overlays.push({
      id: 'jacket-front-roll-line-guide',
      d: buildPolylinePathData([
        { x: patternData.rollLineGuide.p1.x, y: -patternData.rollLineGuide.p1.y },
        { x: patternData.rollLineGuide.p2.x, y: -patternData.rollLineGuide.p2.y },
      ]),
      stroke: '#64748b',
      strokeDasharray: '0.22 0.12',
      previewStrokeWidth: 0.18,
      pdfLineWidth: PDF_STROKE_MM.guide,
      opacity: 0.95,
    });
  }

  if (patternData?.lapelPath) {
    overlays.push({
      id: 'jacket-front-lapel-outline',
      d: patternData.lapelPath,
      stroke: '#0f172a',
      previewStrokeWidth: 0.5,
      pdfLineWidth: PDF_STROKE_MM.main,
      opacity: 0.98,
    });
  }

  if (patternData?.frontDartPath) {
    overlays.push({
      id: 'jacket-front-dart-outline',
      d: patternData.frontDartPath,
      stroke: '#7f1d1d',
      previewStrokeWidth: 0.18,
      pdfLineWidth: PDF_STROKE_MM.main,
      opacity: 0.96,
    });
  }

  const innerLapelPath = buildJacketFrontInnerLapelPath(patternData);
  if (innerLapelPath) {
    overlays.push({
      id: 'jacket-front-inner-lapel-draft',
      d: innerLapelPath,
      stroke: '#475569',
      strokeDasharray: '0.18 0.1',
      previewStrokeWidth: 0.24,
      pdfLineWidth: PDF_STROKE_MM.guide,
      opacity: 0.96,
    });
  }

  if (Array.isArray(patternData?.jacketCollarPaths)) {
    patternData.jacketCollarPaths.forEach((pathOverlay) => {
      overlays.push({
        id: pathOverlay.id,
        d: pathOverlay.d,
        stroke: pathOverlay.id === 'folded-top-collar-preview'
          ? '#64748b'
          : (pathOverlay.stroke || '#0f172a'),
        strokeDasharray: pathOverlay.strokeDasharray,
        previewStrokeWidth: pathOverlay.id === 'folded-top-collar-preview'
          ? 0.18
          : (Number(pathOverlay.strokeWidth) || 0.22),
        pdfLineWidth: pathOverlay.id === 'folded-top-collar-preview'
          ? PDF_STROKE_MM.guide
          : PDF_STROKE_MM.main,
        opacity: pathOverlay.id === 'folded-top-collar-preview' ? 0.92 : 0.98,
      });
    });
  }

  if (Array.isArray(patternData?.jacketShawlFoldedPreviewPaths)) {
    patternData.jacketShawlFoldedPreviewPaths.forEach((pathOverlay) => {
      overlays.push({
        id: pathOverlay.id,
        d: pathOverlay.d,
        stroke: pathOverlay.stroke || '#0f766e',
        strokeDasharray: pathOverlay.strokeDasharray || '0.18 0.1',
        previewStrokeWidth: Number(pathOverlay.strokeWidth) || 0.18,
        pdfLineWidth: PDF_STROKE_MM.guide,
        opacity: 0.92,
      });
    });
  }

  if (Array.isArray(patternData?.jacketPocketMarkers)) {
    patternData.jacketPocketMarkers.forEach((marker) => {
      overlays.push({
        id: marker.id,
        d: marker.d,
        stroke: marker.stroke || '#0f172a',
        previewStrokeWidth: Number(marker.strokeWidth) || 0.2,
        pdfLineWidth: PDF_STROKE_MM.guide,
        opacity: 0.98,
      });
    });
  }

  const lapelType = String(patternData?.lapelType || '').toLowerCase();
  const lb0 = getNamedPointInPathCoords(patternData, 'LB0');
  const lb1 = getNamedPointInPathCoords(patternData, 'LB1');
  const lb0Mirror = getNamedPointInPathCoords(patternData, 'LB0_M');
  const lb1Mirror = getNamedPointInPathCoords(patternData, 'LB1_M');

  if (lapelType === 'shawl') {
    const leftButtonholePath = buildPolylinePathData([lb0, lb1]);
    if (leftButtonholePath) {
      overlays.push({
        id: 'jacket-front-lapel-buttonhole-left',
        d: leftButtonholePath,
        stroke: '#111827',
        previewStrokeWidth: 0.34,
        pdfLineWidth: PDF_STROKE_MM.guide,
      });
    }
  }

  if (lapelType === 'peak' || lapelType === 'shawl') {
    const mirrorButtonholePath = buildPolylinePathData([lb0Mirror, lb1Mirror]);
    if (mirrorButtonholePath) {
      overlays.push({
        id: 'jacket-front-lapel-buttonhole-right',
        d: mirrorButtonholePath,
        stroke: '#111827',
        previewStrokeWidth: 0.34,
        pdfLineWidth: PDF_STROKE_MM.guide,
      });
    }
  }

  return overlays;
}

export function getPieceCurveOverlays(patternData, pieceKey) {
  const segments = Array.isArray(patternData?.curveSegments) ? patternData.curveSegments : [];

  if (pieceKey === 'front') {
    return segments.filter((segment) => segment.id === 'front-hem-curve');
  }

  if (pieceKey === 'back') {
    return segments.filter((segment) => segment.id === 'back-hem-curve');
  }

  return [];
}

function transformedPieceBounds(piecePath, pieceLayout, pieceBounds, extraPaths = []) {
  const paths = [piecePath, ...(Array.isArray(extraPaths) ? extraPaths : [])].filter(Boolean);
  const points = paths.flatMap((path) => extractPathPoints(path));
  if (points.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  }

  const transformed = points.map((point) => transformPoint(point, pieceLayout, pieceBounds));
  return getBounds(transformed);
}

function drawPath(doc, pathData, pieceLayout, pieceBounds, strokeColor = '#111827', lineWidth = PDF_STROKE_MM.main, strokeCmyk = null) {
  const commands = parsePathCommands(pathData);
  if (commands.length === 0) return;

  if (strokeCmyk) {
    setDocCmykColor(doc, strokeCmyk);
  } else {
    doc.setDrawColor(strokeColor);
  }
  doc.setLineWidth(lineWidth);

  let currentRaw = { x: 0, y: 0 };
  let startRaw = null;

  commands.forEach((command) => {
    const type = command[0];
    const args = command
      .slice(1)
      .trim()
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number);

    if (type === 'M') {
      currentRaw = { x: args[0], y: args[1] };
      startRaw = { ...currentRaw };
    } else if (type === 'L') {
      const from = transformPoint(currentRaw, pieceLayout, pieceBounds);
      const targetRaw = { x: args[0], y: args[1] };
      const to = transformPoint(targetRaw, pieceLayout, pieceBounds);
      doc.line(from.x, from.y, to.x, to.y);
      currentRaw = targetRaw;
    } else if (type === 'Q') {
      const p0Raw = { ...currentRaw };
      const p1Raw = { x: args[0], y: args[1] };
      const p2Raw = { x: args[2], y: args[3] };

      let prevPoint = transformPoint(p0Raw, pieceLayout, pieceBounds);
      const steps = 36;
      for (let i = 1; i <= steps; i += 1) {
        const t = i / steps;
        const inv = 1 - t;
        const qx = inv * inv * p0Raw.x + 2 * inv * t * p1Raw.x + t * t * p2Raw.x;
        const qy = inv * inv * p0Raw.y + 2 * inv * t * p1Raw.y + t * t * p2Raw.y;
        const currentPoint = transformPoint({ x: qx, y: qy }, pieceLayout, pieceBounds);
        doc.line(prevPoint.x, prevPoint.y, currentPoint.x, currentPoint.y);
        prevPoint = currentPoint;
      }

      currentRaw = p2Raw;
    } else if (type === 'C') {
      const p0Raw = { ...currentRaw };
      const p1Raw = { x: args[0], y: args[1] };
      const p2Raw = { x: args[2], y: args[3] };
      const p3Raw = { x: args[4], y: args[5] };

      let prevPoint = transformPoint(p0Raw, pieceLayout, pieceBounds);
      const steps = 48;
      for (let i = 1; i <= steps; i += 1) {
        const t = i / steps;
        const inv = 1 - t;
        const cx = inv ** 3 * p0Raw.x
          + 3 * inv ** 2 * t * p1Raw.x
          + 3 * inv * t ** 2 * p2Raw.x
          + t ** 3 * p3Raw.x;
        const cy = inv ** 3 * p0Raw.y
          + 3 * inv ** 2 * t * p1Raw.y
          + 3 * inv * t ** 2 * p2Raw.y
          + t ** 3 * p3Raw.y;

        const currentPoint = transformPoint({ x: cx, y: cy }, pieceLayout, pieceBounds);
        doc.line(prevPoint.x, prevPoint.y, currentPoint.x, currentPoint.y);
        prevPoint = currentPoint;
      }

      currentRaw = p3Raw;
    } else if (type === 'Z' && startRaw) {
      const from = transformPoint(currentRaw, pieceLayout, pieceBounds);
      const to = transformPoint(startRaw, pieceLayout, pieceBounds);
      doc.line(from.x, from.y, to.x, to.y);
      currentRaw = { ...startRaw };
    }
  });
}

function drawPleatGuides(doc, pleatLines, pieceLayout, pieceBounds, stroke = null) {
  if (!pieceLayout || !pleatLines?.length) return;

  pleatLines.forEach((line) => {
    const p1 = transformPoint({ x: line.x1, y: -line.y1 }, pieceLayout, pieceBounds);
    const p2 = transformPoint({ x: line.x2, y: -line.y2 }, pieceLayout, pieceBounds);

    if (stroke?.cmyk) {
      setDocCmykColor(doc, stroke.cmyk);
    } else {
      const isFold = line.type === 'fold' || String(line.type || '').includes('start');
      doc.setDrawColor(isFold ? '#0f172a' : '#475569');
    }
    doc.setLineWidth(PDF_STROKE_MM.guide);
    doc.setLineDashPattern(line.type === 'underlay' ? [1.2, 0.8] : [], 0);
    doc.line(p1.x, p1.y, p2.x, p2.y);
  });

  doc.setLineDashPattern([], 0);
}

function drawFrontPlacketAndButtons(doc, generatedData, frontLayout, frontBounds, stroke = null) {
  if (generatedData.buttonExtensionPath) {
    drawPath(doc, generatedData.buttonExtensionPath, frontLayout, frontBounds, stroke?.hex || '#0f766e', PDF_STROKE_MM.guide, stroke?.cmyk || null);
  }

  if (generatedData.placketFoldPath) {
    drawPath(doc, generatedData.placketFoldPath, frontLayout, frontBounds, stroke?.hex || '#0891b2', PDF_STROKE_MM.guide, stroke?.cmyk || null);
  }

  if (Array.isArray(generatedData.buttonholes)) {
    if (stroke?.cmyk) {
      setDocCmykColor(doc, stroke.cmyk);
    } else {
      doc.setDrawColor(stroke?.hex || '#111827');
    }
    doc.setLineWidth(PDF_STROKE_MM.guide);
    generatedData.buttonholes
      .filter((hole) => !hole.piece || hole.piece === 'front')
      .forEach((hole) => {
      const p1 = transformPoint({ x: hole.start.x, y: hole.start.y }, frontLayout, frontBounds);
      const p2 = transformPoint({ x: hole.end.x, y: hole.end.y }, frontLayout, frontBounds);
      doc.line(p1.x, p1.y, p2.x, p2.y);
    });
  }

  if (Array.isArray(generatedData.buttons)) {
    if (stroke?.cmyk) {
      setDocCmykColor(doc, stroke.cmyk);
    } else {
      doc.setDrawColor(stroke?.hex || '#111827');
    }

    const resolveRadius = (centerRaw, rawRadius) => {
      const edge = transformPoint({ x: centerRaw.x + rawRadius, y: centerRaw.y }, frontLayout, frontBounds);
      const center = transformPoint(centerRaw, frontLayout, frontBounds);
      return {
        center,
        radius: Math.max(0.18, Math.hypot(edge.x - center.x, edge.y - center.y)),
      };
    };

    generatedData.buttons
      .filter((button) => !button.piece || button.piece === 'front')
      .forEach((button) => {
      const outer = resolveRadius({ x: button.x, y: button.y }, 0.22);
      const inner = resolveRadius({ x: button.x, y: button.y }, 0.06);

      doc.setLineWidth(PDF_STROKE_MM.main);
      doc.circle(outer.center.x, outer.center.y, outer.radius, 'S');
      if (stroke?.cmyk) {
        setDocCmykFillColor(doc, stroke.cmyk);
      } else {
        doc.setFillColor(stroke?.hex || '#111827');
      }
      doc.circle(inner.center.x, inner.center.y, inner.radius, 'F');
    });
  }
}

function drawCollarButtonsAndHoles(doc, generatedData, pieceLayout, pieceBounds, stroke = null) {
  const holes = (generatedData.buttonholes || []).filter((hole) => hole.piece === 'collarLayout');
  const buttons = (generatedData.buttons || []).filter((button) => button.piece === 'collarLayout');
  if (!holes.length && !buttons.length) return;

  if (stroke?.cmyk) {
    setDocCmykColor(doc, stroke.cmyk);
  } else {
    doc.setDrawColor(stroke?.hex || '#111827');
  }
  doc.setLineWidth(PDF_STROKE_MM.guide);
  holes.forEach((hole) => {
    const p1 = transformPoint({ x: hole.start.x, y: hole.start.y }, pieceLayout, pieceBounds);
    const p2 = transformPoint({ x: hole.end.x, y: hole.end.y }, pieceLayout, pieceBounds);
    doc.line(p1.x, p1.y, p2.x, p2.y);
  });

  const resolveRadius = (centerRaw, rawRadius) => {
    const edge = transformPoint({ x: centerRaw.x + rawRadius, y: centerRaw.y }, pieceLayout, pieceBounds);
    const center = transformPoint(centerRaw, pieceLayout, pieceBounds);
    return {
      center,
      radius: Math.max(0.18, Math.hypot(edge.x - center.x, edge.y - center.y)),
    };
  };

  buttons.forEach((button) => {
    const outer = resolveRadius({ x: button.x, y: button.y }, 0.22);
    const inner = resolveRadius({ x: button.x, y: button.y }, 0.06);
    if (stroke?.cmyk) {
      setDocCmykColor(doc, stroke.cmyk);
    } else {
      doc.setDrawColor(stroke?.hex || '#111827');
    }
    doc.setLineWidth(PDF_STROKE_MM.main);
    doc.circle(outer.center.x, outer.center.y, outer.radius, 'S');
    if (stroke?.cmyk) {
      setDocCmykFillColor(doc, stroke.cmyk);
    } else {
      doc.setFillColor(stroke?.hex || '#111827');
    }
    doc.circle(inner.center.x, inner.center.y, inner.radius, 'F');
  });
}

function drawConstructionGuides(doc, guides, pieceLayout, pieceBounds, stroke = null) {
  if (!guides?.length) return;

  guides.forEach((guide) => {
    const p1 = transformPoint({ x: guide.p1.x, y: -guide.p1.y }, pieceLayout, pieceBounds);
    const p2 = transformPoint({ x: guide.p2.x, y: -guide.p2.y }, pieceLayout, pieceBounds);

    if (guide.type === 'crease') {
      if (stroke?.cmyk) {
        setDocCmykColor(doc, stroke.cmyk);
      } else {
        doc.setDrawColor('#334155');
      }
      doc.setLineWidth(PDF_STROKE_MM.guide);
      if (doc.setLineDashPattern) {
        doc.setLineDashPattern([2.8, 1.2], 0);
      }
    } else if (guide.type === 'measurement') {
      if (stroke?.cmyk) {
        setDocCmykColor(doc, stroke.cmyk);
      } else {
        doc.setDrawColor('#1f2937');
      }
      doc.setLineWidth(PDF_STROKE_MM.guide);
      if (doc.setLineDashPattern) {
        doc.setLineDashPattern([], 0);
      }
    } else {
      if (stroke?.cmyk) {
        setDocCmykColor(doc, stroke.cmyk);
      } else {
        doc.setDrawColor('#64748b');
      }
      doc.setLineWidth(PDF_STROKE_MM.guide);
      if (doc.setLineDashPattern) {
        doc.setLineDashPattern([2.4, 1.2], 0);
      }
    }

    doc.line(p1.x, p1.y, p2.x, p2.y);

    const labelPlacement = guide.showLabelInPdf === false
      ? null
      : getGuideLabelPlacement(guide, pieceLayout, pieceBounds);
    if (labelPlacement) {
      if (stroke?.cmyk) {
        setDocCmykTextColor(doc, stroke.cmyk);
      } else {
        doc.setTextColor('#1f2937');
      }
      doc.setFont('courier', 'bold');
      doc.setFontSize(8);
      doc.text(labelPlacement.text, labelPlacement.x, labelPlacement.y, {
        angle: labelPlacement.angle,
        align: 'center',
        baseline: 'middle',
      });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
    }
  });

  if (doc.setLineDashPattern) {
    doc.setLineDashPattern([], 0);
  }
}

export function createDefaultLayout(patternData) {
  return withResolvedLabelInset(autoArrangeLayout(patternData, []));
}

export function autoArrangeLayout(patternData, currentLayout = []) {
  const pieces = getExportPieces(patternData);
  const piecesWithBounds = pieces.map((piece) => {
    const existing = currentLayout.find((p) => p.key === piece.key);
    const rotation = Number(existing?.rotation) || 0;

    const basePoints = extractPieceBoundsPoints(piece).map((pt) => {
      const nx = (pt.x - piece.bounds.minX) * INCH_TO_MM;
      const ny = (pt.y - piece.bounds.minY) * INCH_TO_MM;
      return rotatePoint(nx, ny, rotation);
    });

    return {
      key: piece.key,
      label: piece.label,
      path: piece.path,
      rotation,
      flipHorizontal: Boolean(existing?.flipHorizontal),
      flipVertical: Boolean(existing?.flipVertical),
      boundsMm: getBounds(basePoints),
    };
  });

  // Space-saving placement: if exactly two pieces (front/back), place side-by-side when possible.
  if (piecesWithBounds.length === 2) {
    const gap = 18;
    const [a, b] = piecesWithBounds;
    const totalWidth = a.boundsMm.width + b.boundsMm.width + gap;
    const usableWidth = ROLL_WIDTH_MM - MARGIN_MM * 2;

    if (totalWidth <= usableWidth) {
      const startX = (ROLL_WIDTH_MM - totalWidth) / 2;
      const commonY = MARGIN_MM - Math.min(a.boundsMm.minY, b.boundsMm.minY);

      return [
        {
          key: a.key,
          label: a.label,
          path: a.path,
          rotation: a.rotation,
          flipHorizontal: a.flipHorizontal,
          flipVertical: a.flipVertical,
          x: startX - a.boundsMm.minX,
          y: commonY,
        },
        {
          key: b.key,
          label: b.label,
          path: b.path,
          rotation: b.rotation,
          flipHorizontal: b.flipHorizontal,
          flipVertical: b.flipVertical,
          x: startX + a.boundsMm.width + gap - b.boundsMm.minX,
          y: commonY,
        },
      ];
    }
  }

  // Fallback stack layout
  let currentY = MARGIN_MM;
  return piecesWithBounds.map((piece) => {
    const x = Math.max(MARGIN_MM, (ROLL_WIDTH_MM - piece.boundsMm.width) / 2);
    const y = currentY - piece.boundsMm.minY;
    currentY += piece.boundsMm.height + 20;

    return {
      key: piece.key,
      label: piece.label,
      path: piece.path,
      rotation: piece.rotation,
      flipHorizontal: piece.flipHorizontal,
      flipVertical: piece.flipVertical,
      x,
      y,
    };
  });
}

export function compactArrangeLayout(patternData, currentLayout = []) {
  const pieces = getExportPieces(patternData);
  if (pieces.length !== 2) {
    return autoArrangeLayout(patternData, currentLayout);
  }

  const first = pieces[0];
  const second = pieces[1];

  const firstExisting = currentLayout.find((p) => p.key === first.key);
  const secondExisting = currentLayout.find((p) => p.key === second.key);

  // Guided trouser packing profile (based on user-validated nesting approach)
  const frontPiece = pieces.find((p) => p.key === 'front');
  const backPiece = pieces.find((p) => p.key === 'back');

  if (frontPiece && backPiece) {
    // Deterministic user-guided layout:
    // Back: crotch to right, -6° ; Front: legs up/crotch to left, -6°
    const frontDetBase = rotatePoints(normalizeMmPoints(frontPiece).map((pt) => ({ x: -pt.x, y: -pt.y })), -6);
    const backDetBase = rotatePoints(normalizeMmPoints(backPiece), -6);
    const frontDetBounds = getBounds(frontDetBase);
    const backDetBounds = getBounds(backDetBase);

    const rightMargin = 8;
    const leftMargin = 8;
    const minGap = 4;

    const xBackFixed = (ROLL_WIDTH_MM - rightMargin) - backDetBounds.maxX;
    const yBackFixed = MARGIN_MM - backDetBounds.minY;
    const placedBackFixed = translatePoints(backDetBase, xBackFixed, yBackFixed);

    const deterministicGap = 4;
    const deterministicTotalWidth = frontDetBounds.width + backDetBounds.width + deterministicGap;
    const deterministicUsableWidth = ROLL_WIDTH_MM - (leftMargin + rightMargin);
    if (deterministicTotalWidth <= deterministicUsableWidth) {
      const layoutLeft = leftMargin + (deterministicUsableWidth - deterministicTotalWidth) / 2;
      const xFrontLocked = layoutLeft - frontDetBounds.minX;
      const xBackLocked = layoutLeft + frontDetBounds.width + deterministicGap - backDetBounds.minX;
      const yLocked = MARGIN_MM - Math.min(frontDetBounds.minY, backDetBounds.minY);

      return [
        {
          key: frontPiece.key,
          label: frontPiece.label,
          path: frontPiece.path,
          rotation: -6,
          flipHorizontal: true,
          flipVertical: true,
          x: Number(xFrontLocked.toFixed(2)),
          y: Number(yLocked.toFixed(2)),
        },
        {
          key: backPiece.key,
          label: backPiece.label,
          path: backPiece.path,
          rotation: -6,
          flipHorizontal: false,
          flipVertical: false,
          x: Number(xBackLocked.toFixed(2)),
          y: Number(yLocked.toFixed(2)),
        },
      ];
    }

    const xFrontMin = leftMargin - frontDetBounds.minX;
    const xFrontMax = (ROLL_WIDTH_MM - rightMargin) - frontDetBounds.maxX;
    const yShiftCandidates = [0, 8, 16, 24, 32, 40, 56, 72, 88, 104, 120, 140, 160, 180, 200];

    let bestInterlock = null;
    for (const yShift of yShiftCandidates) {
      const yFront = yBackFixed + yShift;

      for (let xFront = xFrontMin; xFront <= xFrontMax; xFront += 4) {
        const placedFront = translatePoints(frontDetBase, xFront, yFront);
        const combined = getBounds([...placedFront, ...placedBackFixed]);

        if (combined.minX < MARGIN_MM || combined.maxX > (ROLL_WIDTH_MM - MARGIN_MM)) continue;
        if (polygonsIntersect(placedFront, placedBackFixed)) continue;

        const frontRight = xFront + frontDetBounds.maxX;
        const backLeft = xBackFixed + backDetBounds.minX;
        const gapPenalty = Math.abs(Math.max(0, backLeft - frontRight) - minGap);
        const score = combined.height + gapPenalty * 0.6 + yShift * 2.2;

        if (!bestInterlock || score < bestInterlock.score) {
          bestInterlock = {
            score,
            xFront,
            yFront,
          };
        }
      }
    }

    if (bestInterlock) {
      return [
        {
          key: frontPiece.key,
          label: frontPiece.label,
          path: frontPiece.path,
          rotation: -6,
          flipHorizontal: true,
          flipVertical: true,
          x: bestInterlock.xFront,
          y: bestInterlock.yFront,
        },
        {
          key: backPiece.key,
          label: backPiece.label,
          path: backPiece.path,
          rotation: -6,
          flipHorizontal: false,
          flipVertical: false,
          x: xBackFixed,
          y: yBackFixed,
        },
      ];
    }

    let xBackDet = (ROLL_WIDTH_MM - rightMargin) - backDetBounds.maxX;
    const backLeftEdge = xBackDet + backDetBounds.minX;
    const xFrontDet = backLeftEdge - frontDetBounds.maxX - minGap;
    const yCommonDet = MARGIN_MM - Math.min(frontDetBounds.minY, backDetBounds.minY);

    const frontLeftEdge = xFrontDet + frontDetBounds.minX;
    const backRightEdge = xBackDet + backDetBounds.maxX;

    if (frontLeftEdge >= leftMargin && backRightEdge <= (ROLL_WIDTH_MM - rightMargin)) {
      return [
        {
          key: frontPiece.key,
          label: frontPiece.label,
          path: frontPiece.path,
          rotation: -6,
          flipHorizontal: true,
          flipVertical: true,
          x: xFrontDet,
          y: yCommonDet,
        },
        {
          key: backPiece.key,
          label: backPiece.label,
          path: backPiece.path,
          rotation: -6,
          flipHorizontal: false,
          flipVertical: false,
          x: xBackDet,
          y: yCommonDet,
        },
      ];
    }

    const flipOptions = [
      { flipHorizontal: true, flipVertical: true },
      { flipHorizontal: false, flipVertical: true },
      { flipHorizontal: true, flipVertical: false },
      { flipHorizontal: false, flipVertical: false },
    ];

    const toFlipped = (points, flipH, flipV) => points.map((pt) => ({
      x: flipH ? -pt.x : pt.x,
      y: flipV ? -pt.y : pt.y,
    }));

    const frontBaseRaw = normalizeMmPoints(frontPiece);
    const backBaseRaw = normalizeMmPoints(backPiece);

    let guidedBest = null;

    flipOptions.forEach((frontFlip) => {
      const frontPoly = rotatePoints(toFlipped(frontBaseRaw, frontFlip.flipHorizontal, frontFlip.flipVertical), -6);
      const frontBounds = getBounds(frontPoly);

      flipOptions.forEach((backFlip) => {
        const backPoly = rotatePoints(toFlipped(backBaseRaw, backFlip.flipHorizontal, backFlip.flipVertical), -6);
        const backBounds = getBounds(backPoly);

        const rightMargin = 8;
        const leftMargin = 8;
        const minGap = 4;
        const xBack = (ROLL_WIDTH_MM - rightMargin) - backBounds.maxX;
        const yBack = MARGIN_MM - backBounds.minY;
        const placedBack = translatePoints(backPoly, xBack, yBack);

        const xMin = leftMargin - frontBounds.minX;
        const xClosest = xBack + backBounds.minX - frontBounds.maxX - minGap;

        for (let yShift = 0; yShift <= 200; yShift += 8) {
          const yFront = yBack + yShift;
          let bestXForY = null;

          for (let xFront = xClosest; xFront >= xMin; xFront -= 4) {
            const placedFront = translatePoints(frontPoly, xFront, yFront);
            const combined = getBounds([...placedFront, ...placedBack]);
            if (combined.minX < MARGIN_MM || combined.maxX > (ROLL_WIDTH_MM - MARGIN_MM)) continue;
            if (polygonsIntersect(placedFront, placedBack)) continue;

            bestXForY = xFront;
            break; // closest non-touching x for this y
          }

          if (bestXForY === null) continue;

          const placedFront = translatePoints(frontPoly, bestXForY, yFront);
          const combined = getBounds([...placedFront, ...placedBack]);
          const score = combined.height + (xClosest - bestXForY) * 0.08 + yShift * 2.2;

          if (!guidedBest || score < guidedBest.score) {
            guidedBest = {
              score,
              pieces: [
                {
                  key: frontPiece.key,
                  label: frontPiece.label,
                  path: frontPiece.path,
                  rotation: -6,
                  flipHorizontal: frontFlip.flipHorizontal,
                  flipVertical: frontFlip.flipVertical,
                  x: bestXForY,
                  y: yFront,
                },
                {
                  key: backPiece.key,
                  label: backPiece.label,
                  path: backPiece.path,
                  rotation: -6,
                  flipHorizontal: backFlip.flipHorizontal,
                  flipVertical: backFlip.flipVertical,
                  x: xBack,
                  y: yBack,
                },
              ],
            };
          }
        }
      });
    });

    if (guidedBest?.pieces?.length) return guidedBest.pieces;

    return autoArrangeLayout(patternData, [
      {
        key: frontPiece.key,
        label: frontPiece.label,
        path: frontPiece.path,
        rotation: -6,
        flipHorizontal: true,
        flipVertical: true,
      },
      {
        key: backPiece.key,
        label: backPiece.label,
        path: backPiece.path,
        rotation: -6,
        flipHorizontal: false,
        flipVertical: false,
      },
    ]);
  }

  const preferredTilts = [-10, -8, -6, 6, 8, 10];

  const flipCombos = [
    { flipHorizontal: false, flipVertical: false },
    { flipHorizontal: true, flipVertical: false },
    { flipHorizontal: false, flipVertical: true },
    { flipHorizontal: true, flipVertical: true },
  ];

  const usableWidth = ROLL_WIDTH_MM - MARGIN_MM * 2;
  const firstMmBase = normalizeMmPoints(first);
  const secondMmBase = normalizeMmPoints(second);

  const overlapRatios = [0.2, 0.4, 0.6, 0.8];
  const yShiftRatios = [-0.9, -0.6, -0.3, 0, 0.3, 0.6, 0.9];

  const buildBaseWithFlip = (points, flipH, flipV) => points.map((pt) => ({
    x: flipH ? -pt.x : pt.x,
    y: flipV ? -pt.y : pt.y,
  }));

  const candidatePairs = [
    [first, second, firstExisting, secondExisting],
    [second, first, secondExisting, firstExisting],
  ];

  let best = null;
  let bestTilt = null;

  candidatePairs.forEach(([pieceA, pieceB, existingA, existingB]) => {
    const baseA = pieceA.key === first.key ? firstMmBase : secondMmBase;
    const baseB = pieceB.key === second.key ? secondMmBase : firstMmBase;

    flipCombos.forEach((flipA) => {
      const flippedA = buildBaseWithFlip(baseA, flipA.flipHorizontal, flipA.flipVertical);

      flipCombos.forEach((flipB) => {
        const flippedB = buildBaseWithFlip(baseB, flipB.flipHorizontal, flipB.flipVertical);

        const anglesA = Array.from(new Set([...preferredTilts, Number(existingA?.rotation) || 0, 0]));
        const anglesB = Array.from(new Set([...preferredTilts, Number(existingB?.rotation) || 0, 0]));

        anglesA.forEach((angleA) => {
          const polyA = rotatePoints(flippedA, angleA);
          const boundsA = getBounds(polyA);

          anglesB.forEach((angleB) => {
            const polyBBase = rotatePoints(flippedB, angleB);
            const boundsB = getBounds(polyBBase);

            overlapRatios.forEach((ratio) => {
              const overlapMm = Math.min(boundsA.width, boundsB.width) * ratio;

              const xCandidates = [
                boundsA.maxX - boundsB.minX - overlapMm + COMPACT_GAP_MM,
                boundsA.minX - boundsB.maxX + overlapMm - COMPACT_GAP_MM,
              ];

              xCandidates.forEach((xBBase) => {
                yShiftRatios.forEach((yRatio) => {
                  const yShift = yRatio * Math.max(boundsA.height, boundsB.height) * 0.8;
                  const yB = boundsA.minY + yShift;

                  const placedA = translatePoints(polyA, 0, 0);
                  const placedB = translatePoints(polyBBase, xBBase, yB);
                  if (polygonsIntersect(placedA, placedB)) return;

                  const allPoints = [...placedA, ...placedB];
                  const combined = getBounds(allPoints);
                  if (combined.width > usableWidth) return;

                  const centerOffsetX = (usableWidth - combined.width) / 2;
                  const shiftX = MARGIN_MM - combined.minX + centerOffsetX;
                  const shiftY = MARGIN_MM - combined.minY;

                  const rotatedNearPreferred = Math.abs(Math.abs(angleA) - 8) + Math.abs(Math.abs(angleB) - 8);
                  const score = combined.height + combined.width * 0.08 + rotatedNearPreferred * 0.6;

                  const layoutA = {
                    key: pieceA.key,
                    label: pieceA.label,
                    path: pieceA.path,
                    rotation: angleA,
                    flipHorizontal: flipA.flipHorizontal,
                    flipVertical: flipA.flipVertical,
                    x: shiftX,
                    y: shiftY,
                  };

                  const layoutB = {
                    key: pieceB.key,
                    label: pieceB.label,
                    path: pieceB.path,
                    rotation: angleB,
                    flipHorizontal: flipB.flipHorizontal,
                    flipVertical: flipB.flipVertical,
                    x: xBBase + shiftX,
                    y: yB + shiftY,
                  };

                  const orderedPieces = pieceA.key === first.key
                    ? [layoutA, layoutB]
                    : [layoutB, layoutA];

                  const candidate = {
                    score,
                    pieces: orderedPieces,
                    bothTilted: Math.abs(angleA) >= 6 && Math.abs(angleA) <= 10 && Math.abs(angleB) >= 6 && Math.abs(angleB) <= 10,
                  };

                  if (!best || candidate.score < best.score) {
                    best = candidate;
                  }
                  if (candidate.bothTilted && (!bestTilt || candidate.score < bestTilt.score)) {
                    bestTilt = candidate;
                  }
                });
              });
            });
          });
        });
      });
    });
  });

  return bestTilt?.pieces || best?.pieces || autoArrangeLayout(patternData, currentLayout);
}

export function getLayoutDiagnostics(patternData, layoutPieces = [], cutLengthInches = null) {
  const pieces = getExportPieces(patternData);
  const pieceMap = new Map(pieces.map((piece) => [piece.key, piece]));
  const activeLayout = layoutPieces.filter((pieceLayout) => pieceMap.has(pieceLayout.key) && isPrintPieceVisible(pieceLayout));

  const requiredLengthInches = estimateRollLengthInches(patternData, activeLayout);
  const effectiveCutLengthInches = Number(cutLengthInches) > 0 ? Number(cutLengthInches) : requiredLengthInches;
  const cutLengthMm = effectiveCutLengthInches * INCH_TO_MM;

  const warnings = [];
  const toleranceMm = 8;

  activeLayout.forEach((pieceLayout) => {
    const piece = pieceMap.get(pieceLayout.key);
    const bounds = transformedPieceBounds(piece.path, pieceLayout, piece.bounds, piece.boundsPaths?.slice(1) || []);

    if (bounds.minX < 0 || bounds.maxX > ROLL_WIDTH_MM) {
      warnings.push(`${piece.label} exceeds A1 width border`);
    }

    if (bounds.maxY > cutLengthMm + toleranceMm) {
      warnings.push(`${piece.label} exceeds roll length border`);
    }
  });

  if (effectiveCutLengthInches < requiredLengthInches) {
    warnings.push(`Cut length is shorter than required (${requiredLengthInches.toFixed(2)}")`);
  }

  return {
    requiredLengthInches,
    effectiveCutLengthInches,
    warnings,
  };
}

export async function generatePDF({ patternData, currentPattern, measurements, controls, layoutConfig, previewSvgElement = null, exportOptions = {} }) {
  const mergedPatternData = patternData
    ? {
        ...patternData,
        measurements: {
          ...((patternData?.measurements && typeof patternData.measurements === 'object') ? patternData.measurements : {}),
          ...((measurements && typeof measurements === 'object') ? measurements : {}),
        },
      }
    : null;
  const generatedData = mergedPatternData || generatePattern(measurements, controls, currentPattern?.garment_type || 'trouser');
  const pieces = getExportPieces(generatedData);
  const layout = layoutConfig?.pieces?.length ? layoutConfig.pieces : createDefaultLayout(generatedData);

  const pieceMap = new Map(pieces.map((piece) => [piece.key, piece]));
  const activeLayout = withResolvedLabelInset(layout.filter((pieceLayout) => pieceMap.has(pieceLayout.key) && isPrintPieceVisible(pieceLayout)));

  const diagnostics = getLayoutDiagnostics(generatedData, activeLayout, layoutConfig?.cutLengthInches);
  const hardCutLengthIn = Math.max(diagnostics.requiredLengthInches, diagnostics.effectiveCutLengthInches);
  const rollLengthMm = hardCutLengthIn * INCH_TO_MM;
  const globalStroke = resolveGlobalExportStroke(exportOptions);

  const doc = new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format: [rollLengthMm, ROLL_WIDTH_MM],
  });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setLineJoin('round');
  doc.setLineCap('round');
  setDocCmykColor(doc, globalStroke.cmyk);
  setDocCmykFillColor(doc, globalStroke.cmyk);
  setDocCmykTextColor(doc, globalStroke.cmyk);

  const labelDrawQueue = [];
  activeLayout.forEach((pieceLayout) => {
    const piece = pieceMap.get(pieceLayout.key);
    const resolvedPieceSettings = resolvePiecePrintSettings(pieceLayout);
    const guides = getPieceConstructionGuides(generatedData, piece.key);
    const pathOverlays = getPiecePathOverlays(generatedData, piece.key);
    const curveOverlays = getPieceCurveOverlays(generatedData, piece.key);
    const pieceNotches = Array.isArray(generatedData?.notchMarks)
      ? generatedData.notchMarks.filter((mark) => mark.piece === piece.key)
      : [];

    drawPath(doc, piece.path, pieceLayout, piece.bounds, globalStroke.hex, PDF_STROKE_MM.main, globalStroke.cmyk);
    drawConstructionGuides(doc, guides, pieceLayout, piece.bounds, globalStroke);

    if (piece.key === 'front' && generatedData.pleatLines?.length) {
      drawPleatGuides(doc, generatedData.pleatLines, pieceLayout, piece.bounds, globalStroke);
    }

    if (piece.key === 'back' && generatedData.backPleatLines?.length) {
      drawPleatGuides(doc, generatedData.backPleatLines, pieceLayout, piece.bounds, globalStroke);
    }

    curveOverlays.forEach((segment) => {
      if (segment.strokeDasharray && doc.setLineDashPattern) {
        doc.setLineDashPattern([1.2, 0.8], 0);
      }
      drawPath(
        doc,
        segment.d,
        pieceLayout,
        piece.bounds,
        globalStroke.hex,
        Number(segment.pdfLineWidth ?? segment.strokeWidth) || PDF_STROKE_MM.main,
        globalStroke.cmyk,
      );
      if (segment.strokeDasharray && doc.setLineDashPattern) {
        doc.setLineDashPattern([], 0);
      }
    });

    pathOverlays.forEach((overlay) => {
      if (overlay.strokeDasharray && doc.setLineDashPattern) {
        doc.setLineDashPattern([1.2, 0.8], 0);
      }
      drawPath(
        doc,
        overlay.d,
        pieceLayout,
        piece.bounds,
        globalStroke.hex,
        Number(overlay.pdfLineWidth ?? overlay.strokeWidth) || PDF_STROKE_MM.main,
        globalStroke.cmyk,
      );
      if (overlay.strokeDasharray && doc.setLineDashPattern) {
        doc.setLineDashPattern([], 0);
      }
    });

    if (piece.key === 'front') {
      drawFrontPlacketAndButtons(doc, generatedData, pieceLayout, piece.bounds, globalStroke);
    }

    if (piece.key === 'collar') {
      drawCollarButtonsAndHoles(doc, generatedData, pieceLayout, piece.bounds, globalStroke);
    }

    if (piece.key === 'sleeve' && generatedData.sleevePlacketPath) {
      drawPath(doc, generatedData.sleevePlacketPath, pieceLayout, piece.bounds, globalStroke.hex, PDF_STROKE_MM.guide, globalStroke.cmyk);
    }

    if (piece.key === 'back' && generatedData.dartPath) {
      drawPath(doc, generatedData.dartPath, pieceLayout, piece.bounds, globalStroke.hex, PDF_STROKE_MM.main, globalStroke.cmyk);
    }

    if (piece.key === 'yoke' && generatedData.yokeDartPath) {
      drawPath(doc, generatedData.yokeDartPath, pieceLayout, piece.bounds, globalStroke.hex, PDF_STROKE_MM.main, globalStroke.cmyk);
    }

    pieceNotches.forEach((mark) => {
      (mark.paths || []).forEach((notchPath) => {
        const notchBounds = transformedPieceBounds(notchPath, pieceLayout, piece.bounds);
        const notchScale = Math.max(notchBounds.width, notchBounds.height);
        const notchStroke = notchScale > 2.2 ? 0.36 : 0.32;
        doc.setLineCap('butt');
        drawPath(doc, notchPath, pieceLayout, piece.bounds, globalStroke.hex, notchStroke, globalStroke.cmyk);
        doc.setLineCap('round');
      });
    });

    const labelText = resolvePieceLabelText(currentPattern, resolvedPieceSettings);
    if (labelText) {
      const labelPlacement = getPieceLabelPlacement(
        generatedData,
        piece.key,
        pieceLayout,
        piece.bounds,
        piece.path,
      );
      labelDrawQueue.push({
        text: labelText,
        placement: labelPlacement,
        pieceSettings: resolvedPieceSettings,
      });
    }
  });

  labelDrawQueue.forEach((labelItem) => {
    setDocCmykTextColor(doc, globalStroke.cmyk);
    drawPatternPieceLabel(doc, labelItem.text, labelItem.placement, labelItem.pieceSettings);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
  });

  const safePatternNumber = currentPattern?.pattern_number || 'pattern';
  doc.save(`${safePatternNumber}_A1_roll.pdf`);
}

export async function generatePDFFromPreviewSVG({
  previewSvgElement,
  currentPattern,
  cutLengthInches,
  requiredLengthInches,
}) {
  if (!previewSvgElement) return false;

  const effectiveCutLengthInches = Number(cutLengthInches) > 0
    ? Number(cutLengthInches)
    : Number(requiredLengthInches) || 10;

  const rollLengthMm = effectiveCutLengthInches * INCH_TO_MM;

  const pageDoc = new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format: [rollLengthMm, ROLL_WIDTH_MM],
  });

  const svgClone = previewSvgElement.cloneNode(true);
  svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svgClone);
  const svgBase64 = window.btoa(unescape(encodeURIComponent(svgString)));
  const svgDataUrl = `data:image/svg+xml;base64,${svgBase64}`;

  const image = new Image();
  image.decoding = 'sync';

  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
    image.src = svgDataUrl;
  });

  const renderScale = 3;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.naturalWidth * renderScale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * renderScale));

  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) return false;

  ctx.fillStyle = '#f8fafc';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
  ctx.drawImage(image, 0, 0);

  const pageWidth = pageDoc.internal.pageSize.getWidth();
  const pageHeight = pageDoc.internal.pageSize.getHeight();
  const pngData = canvas.toDataURL('image/png', 1.0);

  pageDoc.addImage(pngData, 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'SLOW');

  const safePatternNumber = currentPattern?.pattern_number || 'pattern';
  pageDoc.save(`${safePatternNumber}_A1_roll.pdf`);
  return true;
}

export function estimateRollLengthInches(patternData, layoutPieces = []) {
  const pieces = getExportPieces(patternData);
  const pieceMap = new Map(pieces.map((piece) => [piece.key, piece]));
  const activeLayout = layoutPieces.filter((pieceLayout) => pieceMap.has(pieceLayout.key) && isPrintPieceVisible(pieceLayout));

  if (!activeLayout.length) return 0;

  const transformedBounds = activeLayout.map((pieceLayout) => {
    const piece = pieceMap.get(pieceLayout.key);
    return transformedPieceBounds(piece.path, pieceLayout, piece.bounds, piece.boundsPaths?.slice(1) || []);
  });

  const maxY = transformedBounds.length ? Math.max(...transformedBounds.map((b) => b.maxY)) : 0;
  const contentLengthMm = maxY + MARGIN_MM;
  const contentLengthIn = contentLengthMm / INCH_TO_MM;
  return Math.ceil(contentLengthIn * 100) / 100;
}

export function getRecommendedCutLengthInches(requiredLengthInches, allowanceInches = 0.5) {
  const required = Math.max(0, Number(requiredLengthInches) || 0);
  const buffered = required + Math.max(0, Number(allowanceInches) || 0);
  return Math.ceil(buffered * 4) / 4;
}

export function clampLayoutPiecesToViewport(patternData, layoutPieces = [], cutLengthInches = null, paddingMm = 8) {
  const pieces = getExportPieces(patternData);
  const pieceMap = new Map(pieces.map((piece) => [piece.key, piece]));
  const requiredLength = estimateRollLengthInches(patternData, layoutPieces);
  const cutLengthMm = (Number(cutLengthInches) > 0 ? Number(cutLengthInches) : requiredLength) * INCH_TO_MM;
  const minX = paddingMm;
  const maxX = ROLL_WIDTH_MM - paddingMm;
  const minY = paddingMm;
  const maxY = Math.max(minY, cutLengthMm - paddingMm);

  return layoutPieces.map((pieceLayout) => {
    const piece = pieceMap.get(pieceLayout.key);
    if (!piece) return pieceLayout;

    const bounds = transformedPieceBounds(piece.path, pieceLayout, piece.bounds, piece.boundsPaths?.slice(1) || []);
    let dx = 0;
    let dy = 0;

    if (bounds.width >= (maxX - minX)) {
      dx = minX - bounds.minX;
    } else if (bounds.minX < minX) {
      dx = minX - bounds.minX;
    } else if (bounds.maxX > maxX) {
      dx = maxX - bounds.maxX;
    }

    if (bounds.height >= (maxY - minY)) {
      dy = minY - bounds.minY;
    } else if (bounds.minY < minY) {
      dy = minY - bounds.minY;
    } else if (bounds.maxY > maxY) {
      dy = maxY - bounds.maxY;
    }

    if (dx === 0 && dy === 0) return pieceLayout;

    return {
      ...pieceLayout,
      x: Number(((Number(pieceLayout.x) || 0) + dx).toFixed(2)),
      y: Number(((Number(pieceLayout.y) || 0) + dy).toFixed(2)),
    };
  });
}
